import React, { useState, useEffect } from "react";
import { format } from "date-fns";

import api from "../../Utils/api";
import { toast } from "react-toastify";
import { APIProvider } from "@vis.gl/react-google-maps";
import MapDisplay from "./MapDisplay";

// Helper to ensure proper date object
const timeFormat = (timeStr) => {
  const time = new Date(timeStr);
  time.setFullYear(time.getUTCFullYear());
  time.setMonth(time.getUTCMonth());
  time.setDate(time.getUTCDate());
  time.setHours(time.getUTCHours());
  time.setMinutes(time.getUTCMinutes());
  time.setSeconds(time.getUTCSeconds());
  return time;
};

const ScheduleDetailsForm = ({ schedule, locations, getTimesheets }) => {
  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const date = new Date(timeString);

    // Format the time using Intl.DateTimeFormat
    return new Intl.DateTimeFormat([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // use false for 24-hour format, true for AM/PM
      timeZone: "UTC", // optional: remove if you want local time
    }).format(date);
  };

  const [form, setForm] = useState({
    date: "",
    area: "",
    startTime: "",
    endTime: "",
    breakStartTime: "",
    breakEndTime: "",
    comment: "",
  });

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  useEffect(() => {
    setForm({
      date: formatDate(schedule.startTime),
      area: schedule.locationID?.id,
      startTime: formatTime(schedule.clockIn),
      endTime: formatTime(schedule.clockOut),
      breakStartTime: formatTime(schedule.breakClockIn),
      breakEndTime: formatTime(schedule.breakClockOut),
      comment: "",
    });
  }, [schedule]);

  const combineDateTimeUTC = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const [hours, minutes] = timeStr.split(":");
    const [year, month, day] = dateStr.split("-").map(Number);

    // Create a UTC-based date
    const date = new Date(
      Date.UTC(year, month - 1, day, +hours, +minutes, 0, 0)
    );
    return date.toISOString(); // This will now be exactly what you set, in UTC
  };

  const validateTimesheet = () => {
    let scheduleDate = format(timeFormat(schedule.startTime), "yyyy-MM-dd");
    let scheduleLoction = schedule.locationID?.id;

    if (!form.date || !form.area || !form.startTime || !form.endTime) {
      toast.info("Please fill all fields!");
      return false;
    }

    let startTime = new Date(
      combineDateTimeUTC(form.date, form.startTime)
    ).getTime();
    let endTime = new Date(
      combineDateTimeUTC(form.date, form.endTime)
    ).getTime();

    if (startTime > endTime) {
      toast.warn("Invalid Start/End time info!");
      return false;
    }

    if (scheduleDate !== form.date || scheduleLoction !== form.area) {
      toast.error("Invalid Information! You have to check Schedule Details!");
      return false;
    }

    return true;
  };

  const approveTimesheet = async () => {
    let valid = validateTimesheet();
    if (!valid) return;

    const confirmDelete = window.confirm(`Are you sure to approve this shift?`);

    if (!confirmDelete) return;

    const res = await api.post("/schedule/approve", {
      scheduleId: schedule._id,
      userID: schedule.userID.id,
      locationID: schedule.locationID.id,
      startTime: combineDateTimeUTC(form.date, form.startTime),
      endTime: combineDateTimeUTC(form.date, form.endTime),
      startBreakTime: form.startBreakTime
        ? combineDateTimeUTC(form.date, form.startBreakTime)
        : null,
      endBreakTime: form.endBreakTime
        ? combineDateTimeUTC(form.date, form.endBreakTime)
        : null,
      comment: form.comment,
    });

    if (res.data.success) {
      getTimesheets();
      toast.success(res.data.message);
    } else {
      toast.error(res.data.message);
    }
  };

  const deleteTimesheet = async () => {
    const confirmDelete = window.confirm(`Are you sure you want to delete?`);

    const res = await api.delete(`/schedule/${schedule._id}`);

    if (res.data.success) {
      toast.success(res.data.message);
      getTimesheets();
    } else {
      toast.error(res.data.message);
    }
  };

  return (
    <div className="w-full flex flex-wrap justify-between items-start h-fit">
      <div className="w-[calc(100%-300px)]">
        {/* Summary Header */}
        <div className="w-full">
          <div className="bg-gray-100 p-4 w-fit rounded-md">
            <span className="font-semibold">Scheduled Details</span>:{" "}
            {schedule?.startTime &&
              format(timeFormat(schedule.startTime), "EEEEEEE, d, MMMM, yyyy")}
            {" | "}
            {"[" +
              (schedule.locationID?.area || "") +
              (schedule.locationID?.name
                ? "] " + schedule.locationID.name
                : "")}
            {" | "}
            {schedule?.startTime
              ? format(timeFormat(schedule.startTime), "hh:mm aa")
              : ""}{" "}
            ~{" "}
            {schedule?.endTime
              ? format(timeFormat(schedule.endTime), "hh:mm aa")
              : ""}
          </div>
        </div>

        {/* Form Section */}
        <div className="mt-12 w-full flex items-start justify-between">
          <div>
            {/* Date */}
            <FormRow label="Date">
              <input
                type="date"
                value={form.date}
                onChange={handleChange("date")}
                className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 text-gray-700 w-full"
              />
            </FormRow>

            {/* Area */}
            <FormRow label="Area">
              <select
                value={form.area}
                onChange={handleChange("area")}
                className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 text-gray-700 w-full"
              >
                <option value="">Select a location</option>
                {locations.map((loc) => (
                  <option key={loc._id} value={loc._id}>
                    [{loc.area}] {loc.name}
                  </option>
                ))}
              </select>
            </FormRow>

            {/* Start / Finish */}
            <FormRow label="Start/Finish">
              <div className="flex w-full justify-between gap-2">
                <input
                  type="time"
                  value={form.startTime}
                  onChange={handleChange("startTime")}
                  className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 text-gray-700 w-[45%]"
                />
                <input
                  type="time"
                  value={form.endTime}
                  onChange={handleChange("endTime")}
                  className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 text-gray-700 w-[45%]"
                />
              </div>
            </FormRow>

            {/* Clock In / Out */}
            <FormRow label="Break Start/Finish">
              <div className="flex w-full justify-between gap-2">
                <input
                  type="time"
                  value={form.breakStartTime}
                  onChange={handleChange("breakStartTime")}
                  className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 text-gray-700 w-[45%]"
                />
                <input
                  type="time"
                  value={form.breakEndTime}
                  onChange={handleChange("breakEndTime")}
                  className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 text-gray-700 w-[45%]"
                />
              </div>
            </FormRow>

            {/* Comment */}
            <FormRow label="Comment">
              <textarea
                rows={2}
                value={form.comment}
                onChange={handleChange("comment")}
                className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 text-gray-700 w-full"
              />
            </FormRow>
          </div>
          <div className="w-[300px] mr-8 -mt-24 rounded-xl overflow-hidden">
            <APIProvider apiKey={process.env.REACT_APP_GEO_KEY}>
              <MapDisplay gpsCapture={schedule.gpsCapture} />
            </APIProvider>
          </div>
        </div>
      </div>

      <div className="w-[300px] pr-6 bg-white rounded-2xl space-y-6">
        <div className="w-full flex items-center">
          <button
            onClick={approveTimesheet}
            className="px-4 w-full py-2 bg-green-600 mr-4 text-white font-medium rounded-xl hover:bg-gray-700 transition-colors"
          >
            Approve
          </button>
        </div>

        <div className="w-full flex items-center">
          <button
            onClick={deleteTimesheet}
            className="px-4 w-full py-2 bg-red-400 mr-4 text-white font-medium rounded-xl hover:bg-gray-700 transition-colors"
          >
            Delete
          </button>
        </div>

        <div className="flex items-center justify-start">
          <span className="text-sm font-medium text-gray-600 mr-4">Status</span>
          {schedule.status && (
            <StatusTag
              payAmount={schedule.payAmount}
              status={schedule.status}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Small form label/row wrapper
const FormRow = ({ label, children }) => (
  <div className="w-full flex items-center gap-8 mb-2">
    <div className="w-[200px] text-right text-gray-600 font-medium">
      {label}
    </div>
    {children}
  </div>
);

const statusColors = {
  created: {
    background: "bg-sky-100",
    text: "text-sky-800",
  },
  started: {
    background: "bg-red-100",
    text: "text-red-800",
  },
  pending: {
    background: "bg-orange-300",
    text: "text-orange-900",
  },
  approved: {
    background: "bg-green-100",
    text: "text-green-800",
  },
};

function StatusTag({ status, payAmount }) {
  const lowerStatus = status.toLowerCase();
  const colors = statusColors[lowerStatus] || {
    background: "bg-gray-200",
    text: "text-gray-700",
  };

  return (
    <span
      className={`inline-block px-3 py-1 rounded-full font-light ${colors.background} ${colors.text} select-none`}
      aria-label={`Status: ${status}`}
      role="status"
    >
      {status == "approved" && (
        <span className="mr-2 font-semibold">${payAmount}</span>
      )}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
export default ScheduleDetailsForm;
